import React from 'react';
import { Text, View, StyleSheet, Link } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  title: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 14,
    color: '#4E73C3',
    marginBottom: 16,
  },
  paragraph: {
    color: '#000',
    lineHeight: 1.5,
    letterSpacing: -0.32,
  },
  bullet: {
    fontSize: 12,
    marginRight: 8,
  },
});

const SeventhPage = () => {
  const listItems = [
    <Text>
      particulate matter (PM<Text style={{ fontSize: 8 }}>10</Text>);
    </Text>,
    <Text>
      fine particulate matter (PM<Text style={{ fontSize: 8 }}>2.5</Text>);
    </Text>,
    <Text>
      ozone (O<Text style={{ fontSize: 8 }}>3</Text>);
    </Text>,
    <Text>
      nitrogen dioxide (NO<Text style={{ fontSize: 8 }}>2</Text>);
    </Text>,
  ];

  return (
    <View style={{ display: 'flex', flexDirection: 'column' }} wrap={false}>
      <Text style={styles.title}>What is the Air Quality Index?</Text>
      <Text style={{ ...styles.paragraph, fontSize: 11, marginBottom: 16 }}>
        This report uses the European Air Quality Index (
        <Link
          style={{ color: '#000' }}
          src="https://airindex.eea.europa.eu/AQI/index.html#"
        >
          https://airindex.eea.europa.eu/AQI/index.html#
        </Link>
        ). This has been chosen because it offers intuitive health bands,
        providing greater granularity and more detailed insight into air quality
        fluctuations.
      </Text>
      <Text style={{ ...styles.paragraph, fontSize: 11, marginBottom: 16 }}>
        The European Air Quality Index allows users to understand more about air
        quality where they live, work or travel.
      </Text>
      <Text style={{ ...styles.paragraph, fontSize: 11, marginBottom: 16 }}>
        Within this report, the Index is based on pollutant concentration values
        for up to four key pollutants measured by the Zephyr® air quality
        monitor installed at your school:
      </Text>
      <View style={{ marginBottom: 16, paddingLeft: 50 }}>
        {listItems.map((item, idx) => (
          <View
            key={idx}
            style={{ flexDirection: 'row', alignItems: 'center' }}
          >
            <Text style={styles.bullet}>•</Text>
            <Text style={{ fontSize: 11 }}>{item}</Text>
          </View>
        ))}
      </View>
      <Text style={{ ...styles.paragraph, fontSize: 11, marginBottom: 16 }}>
        In this report, AQIs are calculated using hourly averages for all
        pollutants. This method provides clearer insight into when pollution
        levels are elevated, making it easier to identify specific sources or
        events that may be contributing to poor air quality.
      </Text>
      <Text style={{ ...styles.paragraph, fontSize: 11, marginBottom: 46 }}>
        The index bands are complemented by health related messages that provide
        recommendations for both the general population and sensitive
        populations. The latter includes both adults and children with
        respiratory problems and adults with heart conditions.
      </Text>
      <View
        style={{
          width: '100%',
          maxWidth: 499,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          padding: 8,
          borderBottom: '1px solid #CBD5E0',
        }}
      >
        <Text
          style={{
            width: 80,
            textAlign: 'right',
            flexShrink: 0,
            marginRight: 8,
            fontSize: 11,
            fontFamily: 'Helvetica-Bold',
          }}
        >
          AQI Band
        </Text>
        <Text
          style={{
            ...styles.paragraph,
            fontSize: 11,
            fontFamily: 'Helvetica-Bold',
            width: '100%',
            maxWidth: 193.5,
            marginRight: 8,
          }}
        >
          Sensitive Populations
        </Text>
        <Text
          style={{
            ...styles.paragraph,
            fontSize: 11,
            fontFamily: 'Helvetica-Bold',
            width: '100%',
            maxWidth: 193.5,
          }}
        >
          General Population
        </Text>
      </View>
      <View
        style={{
          width: '100%',
          maxWidth: 499,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          padding: 8,
          backgroundColor: '#F7FAFC',
        }}
      >
        <Text
          style={{
            width: 80,
            textAlign: 'right',
            flexShrink: 0,
            marginRight: 8,
            fontSize: 11,
            fontFamily: 'Helvetica-Bold',
          }}
        >
          Good
        </Text>
        <Text
          style={{
            ...styles.paragraph,
            fontSize: 11,
            width: '100%',
            maxWidth: 193.5,
            marginRight: 8,
          }}
        >
          The air quality is good. Enjoy your usual outdoor activities
        </Text>
        <Text
          style={{
            ...styles.paragraph,
            fontSize: 11,
            width: '100%',
            maxWidth: 193.5,
          }}
        >
          The air quality is good. Enjoy your usual outdoor activities
        </Text>
      </View>
      <View
        style={{
          width: '100%',
          maxWidth: 499,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          padding: 8,
        }}
      >
        <Text
          style={{
            width: 80,
            textAlign: 'right',
            flexShrink: 0,
            marginRight: 8,
            fontSize: 11,
            fontFamily: 'Helvetica-Bold',
          }}
        >
          Fair
        </Text>
        <Text
          style={{
            ...styles.paragraph,
            fontSize: 11,
            width: '100%',
            maxWidth: 193.5,
            marginRight: 8,
          }}
        >
          Enjoy your usual outdoor activities
        </Text>
        <Text
          style={{
            ...styles.paragraph,
            fontSize: 11,
            width: '100%',
            maxWidth: 193.5,
          }}
        >
          Enjoy your usual outdoor activities
        </Text>
      </View>
      <View
        style={{
          width: '100%',
          maxWidth: 499,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          padding: 8,
          backgroundColor: '#F7FAFC',
        }}
      >
        <Text
          style={{
            width: 80,
            textAlign: 'right',
            flexShrink: 0,
            marginRight: 8,
            fontSize: 11,
            fontFamily: 'Helvetica-Bold',
          }}
        >
          Moderate
        </Text>
        <Text
          style={{
            ...styles.paragraph,
            fontSize: 11,
            width: '100%',
            maxWidth: 193.5,
            marginRight: 8,
          }}
        >
          Consider reducing intense outdoor activities, if you experience
          symptoms
        </Text>
        <Text
          style={{
            ...styles.paragraph,
            fontSize: 11,
            width: '100%',
            maxWidth: 193.5,
          }}
        >
          Enjoy your usual outdoor activities
        </Text>
      </View>
      <View
        style={{
          width: '100%',
          maxWidth: 499,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          padding: 8,
        }}
      >
        <Text
          style={{
            width: 80,
            textAlign: 'right',
            flexShrink: 0,
            marginRight: 8,
            fontSize: 11,
            fontFamily: 'Helvetica-Bold',
          }}
        >
          Poor
        </Text>
        <Text
          style={{
            ...styles.paragraph,
            fontSize: 11,
            width: '100%',
            maxWidth: 193.5,
            marginRight: 8,
          }}
        >
          Consider reducing physical activities, particularly outdoors,
          especially if you experience symptoms
        </Text>
        <Text
          style={{
            ...styles.paragraph,
            fontSize: 11,
            width: '100%',
            maxWidth: 193.5,
          }}
        >
          Consider reducing intense activities outdoors, if you experience
          symptoms such as sore eyes, a cough or sore throat
        </Text>
      </View>
      <View
        style={{
          width: '100%',
          maxWidth: 499,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          padding: 8,
          backgroundColor: '#F7FAFC',
        }}
      >
        <Text
          style={{
            width: 80,
            textAlign: 'right',
            flexShrink: 0,
            marginRight: 8,
            fontSize: 11,
            fontFamily: 'Helvetica-Bold',
          }}
        >
          Very Poor
        </Text>
        <Text
          style={{
            ...styles.paragraph,
            fontSize: 11,
            width: '100%',
            maxWidth: 193.5,
            marginRight: 8,
          }}
        >
          Reduce physical activities, particularly outdoors, especially if you
          experience symptoms
        </Text>
        <Text
          style={{
            ...styles.paragraph,
            fontSize: 11,
            width: '100%',
            maxWidth: 193.5,
          }}
        >
          Consider reducing intense activities outdoors, if you experience
          symptoms such as sore eyes, a cough or sore throat
        </Text>
      </View>
      <View
        style={{
          width: '100%',
          maxWidth: 499,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          padding: 8,
          marginBottom: 16,
        }}
      >
        <Text
          style={{
            width: 80,
            textAlign: 'right',
            flexShrink: 0,
            marginRight: 8,
            fontSize: 11,
            fontFamily: 'Helvetica-Bold',
          }}
        >
          Extremely Poor
        </Text>
        <Text
          style={{
            ...styles.paragraph,
            fontSize: 11,
            width: '100%',
            maxWidth: 193.5,
            marginRight: 8,
          }}
        >
          Avoid physical activities outdoors
        </Text>
        <Text
          style={{
            ...styles.paragraph,
            fontSize: 11,
            width: '100%',
            maxWidth: 193.5,
          }}
        >
          Reduce physical activities outdoors
        </Text>
      </View>
      <Link
        style={{ fontSize: 10, color: '#4A5568' }}
        src="https://airindex.eea.europa.eu/AQI/index.html"
      >
        https://airindex.eea.europa.eu/AQI/index.html
      </Link>
    </View>
  );
};

export default SeventhPage;
